import React, { useEffect, useState, useCallback, useRef } from "react";
import Joyride, { STATUS, EVENTS, ACTIONS } from "react-joyride";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../../api/urls";

const NAVY   = "#221C5D";
const ORANGE = "#E56A22";

// ── Tooltip custom component ──────────────────────────────────────────────────
const CustomTooltip = ({
    continuous,
    index,
    step,
    size,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
}) => (
    <div
        {...tooltipProps}
        style={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: "28px 28px 20px",
            maxWidth: "380px",
            boxShadow: "0 12px 40px rgba(10,49,110,0.14)",
            fontFamily: "inherit",
        }}
    >
        {/* Header bar */}
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
            }}
        >
            <div>
                {step.title && (
                    <div
                        style={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: NAVY,
                            marginBottom: "8px",
                            lineHeight: 1.3,
                        }}
                    >
                        {step.title}
                    </div>
                )}
                {/* Progress dots */}
                <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                    {Array.from({ length: size }).map((_, i) => (
                        <div
                            key={i}
                            style={{
                                width: i === index ? "18px" : "6px",
                                height: "6px",
                                borderRadius: "999px",
                                backgroundColor: i === index ? ORANGE : "#dee2e6",
                                transition: "width .2s ease",
                            }}
                        />
                    ))}
                </div>
            </div>
            {/* Close — turu sonlandırır */}
            <button
                {...closeProps}
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#adb5bd",
                    fontSize: "18px",
                    lineHeight: 1,
                    padding: "0 0 0 12px",
                }}
            >
                ×
            </button>
        </div>

        {/* Content */}
        <p
            style={{
                fontSize: "13.5px",
                color: "#555",
                lineHeight: 1.7,
                margin: "0 0 20px",
            }}
        >
            {step.content}
        </p>

        {/* Footer */}
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
            }}
        >
            {index > 0 && index < size - 1 ? (
                <button
                    {...skipProps}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#adb5bd",
                        fontSize: "12px",
                        padding: 0,
                    }}
                >
                    Turu Atla
                </button>
            ) : (
                <span />
            )}

            <div style={{ display: "flex", gap: "8px" }}>
                {index > 0 && (
                    <button
                        {...backProps}
                        style={{
                            background: "none",
                            border: `1.5px solid ${NAVY}`,
                            borderRadius: "999px",
                            padding: "7px 18px",
                            color: NAVY,
                            fontWeight: 600,
                            fontSize: "13px",
                            cursor: "pointer",
                        }}
                    >
                        ← Geri
                    </button>
                )}
                <button
                    {...primaryProps}
                    style={{
                        background: NAVY,
                        border: `1.5px solid ${NAVY}`,
                        borderRadius: "999px",
                        padding: "7px 20px",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: "pointer",
                    }}
                >
                    {continuous
                        ? index === size - 1
                            ? "🎉 Tamamla"
                            : "İleri →"
                        : "Kapat"}
                </button>
            </div>
        </div>
    </div>
);

// ── Tour adım tanımları ───────────────────────────────────────────────────────
// route: null → mevcut sayfa; route: "/path" → önce o sayfaya navigate et
// allowedRoles: null/[] → herkese göster; dolu → sadece o rollere göster
const ALL_STEPS = [
    {
        target: "body",
        placement: "center",
        disableBeacon: true,
        disableScrolling: true,
        route: null,
        allowedRoles: null,
        title: "👋 Tahsilat360'a Hoş Geldiniz!",
        content:
            "Kısa bir turla uygulamanın temel özelliklerini keşfedelim. Her adımda ilgili sayfaya giderek size göstereceğiz.",
    },
    {
        target: '[data-tour="dashboard-stats"]',
        placement: "bottom",
        disableBeacon: true,
        disableScrolling: true,   // Dashboard'da aşağı kaydırma
        route: "/dashboard",
        allowedRoles: null,
        title: "📊 Kontrol Paneli — Genel Bakış",
        content:
            "Dönemsel tahsilat özetleri, ödeme dağılımı ve grafikleri burada takip edin. Filtrelerle farklı zaman aralıklarını karşılaştırabilirsiniz.",
    },
    {
        target: '[data-tour="accounts-header"]',
        placement: "bottom",
        disableBeacon: true,
        route: "/accounts",
        // Sadece accounts sayfasına erişimi olan roller
        allowedRoles: [
            "ROLE_COMPANY_ADMIN",
            "ROLE_COMPANY_ACCOUNTANT",
            "ROLE_COMPANY_SALESPERSON",
        ],
        title: "👥 Cariler — Hesap Yönetimi",
        content:
            "Tüm cari ve bayi hesaplarınızı buradan yönetin. Yeni cari ekleyebilir, içe/dışa aktarma yapabilir ya da şirkete göre filtreleyebilirsiniz.",
    },
    {
        target: '[data-tour="accounts-table"]',
        placement: "top",
        disableBeacon: true,
        route: "/accounts",
        allowedRoles: [
            "ROLE_COMPANY_ADMIN",
            "ROLE_COMPANY_ACCOUNTANT",
            "ROLE_COMPANY_SALESPERSON",
        ],
        title: "👥 Cariler — Tablo",
        content:
            "Her satırda carinin güncel borç/alacak bakiyesini ve hareket detaylarını görebilirsiniz. Satıra tıklayarak detay sayfasına geçebilirsiniz.",
    },
    {
        target: '[data-tour="payment-links-header"]',
        placement: "bottom",
        disableBeacon: true,
        route: "/payment-links",
        allowedRoles: null,
        title: "🔗 Link ile Ödeme",
        content:
            "Müşterilerinize özel ödeme linki oluşturun. SMS, e-posta veya WhatsApp ile paylaşın; müşteri tıklayarak güvenli biçimde ödeme tamamlar.",
    },
    {
        target: '[data-tour="invoice-tracking-header"]',
        placement: "bottom",
        disableBeacon: true,
        route: "/invoice-tracking",
        allowedRoles: null,
        title: "📅 Vade Takibi — Filtreler",
        content:
            "Vadesi yaklaşan veya geçmiş borçları \"Vadesi Geçmiş\", \"Bekleyen\" veya \"Ödenmiş\" filtreleriyle ayrıştırın.",
    },
    {
        target: '[data-tour="invoice-tracking-table"]',
        placement: "top",
        disableBeacon: true,
        route: "/invoice-tracking",
        allowedRoles: null,
        title: "📅 Vade Takibi — Tablo",
        content:
            "Her satırda borç tutarı, vade tarihi ve cari bilgisi yer alır. Sağdaki butonlarla anında tahsilat başlatabilir ya da ödeme linki oluşturabilirsiniz.",
    },
    {
        target: '[data-tour="payment-history-card"]',
        placement: "top",
        disableBeacon: true,
        route: "/payment-history",
        allowedRoles: null,
        title: "🕐 Ödeme Geçmişi",
        content:
            "Tüm geçmiş tahsilatlarınızı tarih aralığı, durum ve cari bazında filtreleyin. PDF raporu oluşturabilir veya işlem detaylarını inceleyebilirsiniz.",
    },
    {
        target: '[data-tour="subscription-details"]',
        placement: "bottom",
        disableBeacon: true,
        route: "/subscription/details",
        allowedRoles: ["ROLE_COMPANY_ADMIN"],
        title: "📦 Aboneliğim",
        content:
            "Aktif abonelik paketinizi, kalan sürenizi, kullanım haklarınızı (cari sayısı, kullanıcı sayısı) ve plan özelliklerinizi bu sayfadan takip edebilirsiniz.",
    },
    {
        target: '[data-tour="subscription-plans"]',
        placement: "bottom",
        disableBeacon: true,
        route: "/subscription/plans",
        allowedRoles: ["ROLE_COMPANY_ADMIN"],
        title: "📋 Abonelik Paketleri",
        content:
            "Mevcut tüm paketleri karşılaştırın, ihtiyacınıza uygun olanı seçerek yükseltin veya yenileyin. Plan karşılaştırma sayfasından detaylı fark analizi yapabilirsiniz.",
    },
    {
        target: '[data-tour="subscription-history"]',
        placement: "bottom",
        disableBeacon: true,
        route: "/subscription/history",
        allowedRoles: ["ROLE_COMPANY_ADMIN"],
        title: "🧾 Abonelik Geçmişi",
        content:
            "Geçmiş abonelik işlemlerinizi, ödeme tutarlarını ve fatura durumlarını buradan görüntüleyebilirsiniz. Fatura PDF'lerini indirebilirsiniz.",
    },
    {
        target: '[data-tour="settings-sidebar"]',
        placement: "right",
        disableBeacon: true,
        route: "/settings",
        allowedRoles: null,
        title: "⚙️ Ayarlar",
        content:
            "Firma bilgileri, sanal POS entegrasyonları, taksit grupları ve kullanıcı profiliniz bu menüden yönetilir. İstediğiniz sekmeye tıklayarak ilgili ayarlara ulaşabilirsiniz.",
    },
    {
        target: "body",
        placement: "center",
        disableBeacon: true,
        disableScrolling: true,
        route: null,
        allowedRoles: null,
        title: "🎉 Her Şey Hazır!",
        content:
            "Tahsilat360'ı kullanmaya başlayabilirsiniz. Herhangi bir sorunuz olursa destek ekibimiz size yardımcı olmaktan mutluluk duyar.",
    },
];

// ── Ana bileşen ───────────────────────────────────────────────────────────────
const OnboardingTour = ({ blocked = false }) => {
    const [run, setRun]             = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    // Kullanıcı rolüne göre filtrelenmiş adımlar
    const [steps, setSteps]         = useState([]);
    const navigate  = useNavigate();
    const location  = useLocation();
    const blockedRef = useRef(blocked);
    const runTimeoutRef = useRef(null);

    // Tur başlatılabilir mi kontrol et (mount'ta + contractSigned eventi sonrası çağrılır)
    const startTourIfNeeded = useCallback(async () => {
        try {
            if (blockedRef.current) return;

            const res = await axios.get(API.auth_me, {
                withCredentials: true,
                headers: {},
            });
            if (blockedRef.current) return;

            const userData = res.data?.data;

            // Super admin'e onboarding turu gösterme
            if (userData?.roles?.includes("ROLE_SUPER_ADMIN")) return;

            // Önce sözleşme durumunu kontrol et: imzalanmamışsa turu başlatma
            try {
                const contractRes = await axios.get(API.status_contract, {
                    withCredentials: true,
                    headers: {},
                });
                if (blockedRef.current) return;
                const cs = contractRes.data?.data;
                // status === "PENDING" veya signed === false ise bekle
                if (cs?.signed === false || cs?.status === "PENDING") return;
            } catch (e) {
                const httpStatus = e?.response?.status;
                // 401/403: pre-auth token sözleşme status'ına erişemiyor
                // → sözleşme henüz imzalanmamış demek, turu başlatma
                if (httpStatus === 401 || httpStatus === 403) return;
                // 404 veya başka hata: bu tenant'ta sözleşme zorunlu değil → devam et
            }

            // Onboarding tamamlanmamışsa turu başlat
            if (userData?.onboardingCompleted === false) {
                const userRoles = userData?.roles || [];

                // Kullanıcının rolüne göre adımları filtrele
                const isAccountUser = userRoles.some(r => r.startsWith("ROLE_ACCOUNT_"));
                const filtered = ALL_STEPS.filter(step => {
                    if (!step.allowedRoles || step.allowedRoles.length === 0) return true;
                    return step.allowedRoles.some(r => userRoles.includes(r));
                }).map(step => isAccountUser && step.title
                    ? { ...step, title: step.title.replace("Vade Takibi", "Borç Takibi") }
                    : step);

                if (blockedRef.current) return;
                setSteps(filtered);
                if (runTimeoutRef.current) clearTimeout(runTimeoutRef.current);
                runTimeoutRef.current = setTimeout(() => {
                    if (!blockedRef.current) setRun(true);
                }, 900);
            }
        } catch {
            // Auth hatası ya da network sorunu — sessizce geç
        }
    }, []);

    useEffect(() => {
        blockedRef.current = blocked;
    }, [blocked]);

    useEffect(() => {
        // Sözleşme modalı açıkken turu kapat
        if (blocked) {
            if (runTimeoutRef.current) {
                clearTimeout(runTimeoutRef.current);
                runTimeoutRef.current = null;
            }
            setRun(false);
            setStepIndex(0);
            return;
        }

        // Modal kapalıysa turu başlatılabilirlik açısından yeniden değerlendir
        startTourIfNeeded();
    }, [blocked, startTourIfNeeded]);

    useEffect(() => {
        // Sözleşme imzalandıktan sonra tur başlatılabilir mi diye yeniden kontrol et
        const onContractSigned = () => {
            if (!blockedRef.current) startTourIfNeeded();
        };
        window.addEventListener("contractSigned", onContractSigned);
        return () => window.removeEventListener("contractSigned", onContractSigned);
    }, [startTourIfNeeded]);

    useEffect(() => {
        return () => {
            if (runTimeoutRef.current) clearTimeout(runTimeoutRef.current);
        };
    }, []);

    // Turu bitir ve backend'e bildir
    const handleFinish = () => {
        setRun(false);
        setStepIndex(0);

        axios
            .patch(API.complete_onboarding, {}, {
                withCredentials: true,
                headers: {},
            })
            .catch(() => {});
    };

    // Bir sonraki/önceki adıma geç; gerekirse sayfaya navigate et
    const goToStep = (nextIndex, currentSteps) => {
        if (nextIndex < 0 || nextIndex >= currentSteps.length) return;

        const nextStep = currentSteps[nextIndex];

        if (nextStep.route && location.pathname !== nextStep.route) {
            setRun(false);
            navigate(nextStep.route);
            setTimeout(() => {
                setStepIndex(nextIndex);
                setRun(true);
            }, 700);
        } else {
            setStepIndex(nextIndex);
        }
    };

    const handleCallback = ({ action, index, type, status }) => {
        // Joyride'ın kendi FINISHED / SKIPPED olayı (fallback)
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            handleFinish();
            return;
        }

        if (type === EVENTS.STEP_AFTER) {
            // × butonu veya "Turu Atla" → turu sonlandır
            if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
                handleFinish();
                return;
            }

            // Son adımda "Tamamla"
            if (action === ACTIONS.NEXT && index === steps.length - 1) {
                handleFinish();
                return;
            }

            // Normal ileri / geri navigasyon
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            goToStep(nextIndex, steps);
        }

        // Hedef element DOM'da yok → adımı atla
        if (type === EVENTS.TARGET_NOT_FOUND) {
            const nextIndex = index + 1;
            if (nextIndex >= steps.length) {
                handleFinish();
            } else {
                goToStep(nextIndex, steps);
            }
        }
    };

    if (blocked || !run || steps.length === 0) return null;

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showProgress={false}
            showSkipButton={false}
            disableScrolling={false}
            disableOverlayClose
            spotlightClicks={false}
            callback={handleCallback}
            tooltipComponent={CustomTooltip}
            styles={{
                options: {
                    zIndex: 10000,
                    overlayColor: "rgba(10, 49, 110, 0.42)",
                    spotlightShadow: `0 0 0 3px ${ORANGE}, 0 0 0 6px ${ORANGE}33`,
                },
                spotlight: {
                    borderRadius: "10px",
                },
            }}
            floaterProps={{
                disableAnimation: false,
                hideArrow: false,
            }}
        />
    );
};

export default OnboardingTour;
